import { ComponentPropsWithoutRef, forwardRef, PropsWithChildren, ReactNode } from 'react'

import { useIsMobile } from '@/hooks/useIsMobile'
import { cn } from '@/lib/utils'

import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogTitle } from './ui/dialog'

type DialogContentProps = ComponentPropsWithoutRef<typeof DialogContent>
import { Drawer, DrawerContent, DrawerFooter, DrawerHeader, DrawerTitle } from './ui/drawer'
const fullScreenDialogClasses = 'h-[100dvh] max-h-[100dvh] w-[100vw] max-w-none'
interface Props extends PropsWithChildren {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  title?: string | ReactNode
  alwaysDialog?: boolean
  dialogContentClassName?: string
  drawerClassName?: string
  titleClassName?: string
  className?: string
  hideCloseButton?: boolean
  drawerContentClassName?: string
  fullScreenDialog?: 'all' | 'desktop' | 'mobile'
  modal?: boolean
  footerActions?: ReactNode
  footerActionsClassName?: string
  /** Passed to Radix `Dialog.Content`; use `e.preventDefault()` to skip focusing the first field on open. */
  onOpenAutoFocus?: DialogContentProps['onOpenAutoFocus']
  /** Passed to Radix `Dialog.Content`; use `e.preventDefault()` to control focus after close. */
  onCloseAutoFocus?: DialogContentProps['onCloseAutoFocus']
  /** Passed to Radix `Dialog.Content`; use `e.preventDefault()` to keep the dialog open. */
  onInteractOutside?: DialogContentProps['onInteractOutside']
}
export const AppDialog = forwardRef<HTMLDivElement, Props>(
  (
    {
      children,
      title,
      isOpen,
      onOpenChange,
      alwaysDialog,
      dialogContentClassName,
      titleClassName,
      className,
      hideCloseButton,
      drawerContentClassName,
      fullScreenDialog,
      modal = true,
      footerActions,
      drawerClassName,
      onOpenAutoFocus,
      onCloseAutoFocus,
      onInteractOutside,
      footerActionsClassName,
    },
    ref
  ) => {
    const isMobile = useIsMobile()
    if (isMobile && !alwaysDialog) {
      return (
        <Drawer open={isOpen} onOpenChange={onOpenChange}>
          <DrawerContent className={cn('min-h-[20vh] p-0', drawerClassName)}>
            <DrawerHeader className={cn(!title && 'sr-only')}>
              <DrawerTitle className={titleClassName}>{title}</DrawerTitle>
            </DrawerHeader>
            <div className={cn('flex grow flex-col overflow-y-auto px-4 py-4', drawerContentClassName)}>{children}</div>
            {footerActions && <DrawerFooter className={footerActionsClassName}>{footerActions}</DrawerFooter>}
          </DrawerContent>
        </Drawer>
      )
    }

    const dialogClassName = cn(
      'flex max-w-[1200px] flex-col p-0 max-h-[90vh] rounded-[12px] border-none',
      fullScreenDialog === 'all' && fullScreenDialogClasses,
      fullScreenDialog === 'desktop' && !isMobile && fullScreenDialogClasses,
      fullScreenDialog === 'mobile' && isMobile && fullScreenDialogClasses,
      !title && 'pt-6',
      className
    )
    return (
      <Dialog open={isOpen} onOpenChange={onOpenChange} modal={modal}>
        {isOpen && !modal && (
          <div
            className={cn(
              'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/80'
            )}
          ></div>
        )}
        <DialogContent
          showCloseButton={!hideCloseButton}
          ref={ref}
          className={dialogClassName}
          onOpenAutoFocus={onOpenAutoFocus}
          onCloseAutoFocus={onCloseAutoFocus}
          onInteractOutside={onInteractOutside}
        >
          <DialogTitle className={cn('flex-shrink-0 p-6 pb-0', !title && 'sr-only', title && titleClassName)}>
            {title || 'Dialog'}
          </DialogTitle>
          <DialogDescription className='sr-only'>{title || 'Dialog'} content</DialogDescription>
          <div className={cn('min-h-0 flex-1 overflow-y-auto p-6 pt-0', dialogContentClassName)}>{children}</div>
          {footerActions && <DialogFooter className={cn('-mt-4', footerActionsClassName)}>{footerActions}</DialogFooter>}
        </DialogContent>
      </Dialog>
    )
  }
)
AppDialog.displayName = 'AppDialog'
